import { useState, useEffect, useCallback } from 'react'
import { X, Plus, Trash2, RefreshCw, Save, Edit2, History } from 'lucide-react'
import { useAuth } from '../../../context/AuthContext'

export default function YTChannelManager({ channels, onClose, onUpdate, onSync, syncing }) {
  const { api } = useAuth()
  const [editing, setEditing] = useState(null)
  const [showAdd, setShowAdd] = useState(false)
  const [status, setStatus] = useState('')
  const [historyTarget, setHistoryTarget] = useState(null)
  const [historyMonths, setHistoryMonths] = useState(6)
  const [historySyncing, setHistorySyncing] = useState(false)

  // TBTV Channels for picker
  const [tbtvChannels, setTbtvChannels] = useState([])
  // CF Live Inputs for picker
  const [cfLiveInputs, setCfLiveInputs] = useState([])

  const [newChannel, setNewChannel] = useState({
    youtube_channel_id: '', name: '', api_key: '',
    default_duration: 120, default_viloud_channel_name: '',
    default_viloud_id: '', default_cf_input_id: '', enabled: true,
    default_tbtv_channel_id: '',
    auto_migrate_thumbnails: false,  // dev24: opt-in per source
  })

  const [editForm, setEditForm] = useState({})
  const [showManualAdd, setShowManualAdd] = useState(false)
  const [showManualEdit, setShowManualEdit] = useState(false)

  // Load TBTV channels for the picker
  const loadTbtvChannels = useCallback(async () => {
    try {
      const data = await api('/channels')
      setTbtvChannels(data.channels || [])
    } catch (e) {
      console.error('Failed to load TBTV channels:', e)
    }
  }, [api])

  // Load CF Live Inputs for the picker
  const loadCfLiveInputs = useCallback(async () => {
    try {
      const data = await api('/live-inputs')
      setCfLiveInputs(data.liveInputs || [])
    } catch (e) {
      console.error('Failed to load CF live inputs:', e)
    }
  }, [api])

  useEffect(() => { loadTbtvChannels(); loadCfLiveInputs() }, [loadTbtvChannels, loadCfLiveInputs])

  // When a TBTV channel is selected, auto-fill viloud_id, name, and cf_input
  function handleTbtvChannelSelect(tbtvChannelId, formSetter, currentForm) {
    if (!tbtvChannelId) {
      formSetter({ ...currentForm, default_tbtv_channel_id: '' })
      return
    }
    const ch = tbtvChannels.find(c => c.tbtv_channel_id === tbtvChannelId)
    if (!ch) return
    formSetter({
      ...currentForm,
      default_tbtv_channel_id: tbtvChannelId,
      default_viloud_id: ch.viloud_id || '',
      default_viloud_channel_name: ch.name || '',
      default_cf_input_id: ch.cf_live_input_id || currentForm.default_cf_input_id || '',
    })
  }

  // Resolve tbtv_channel_id to channel name for display
  function getTbtvChannelDisplay(tbtvId) {
    if (!tbtvId) return null
    const ch = tbtvChannels.find(c => c.tbtv_channel_id === tbtvId)
    return ch ? ch.name : tbtvId
  }

  // Resolve CF input UID to name for display
  function getCfInputDisplay(uid) {
    if (!uid) return null
    const li = cfLiveInputs.find(i => i.uid === uid)
    return li ? (li.meta?.name || uid.substring(0, 8)) : uid.substring(0, 8)
  }

  async function handleAdd() {
    if (!newChannel.youtube_channel_id || !newChannel.name) {
      setStatus('Channel ID and name are required')
      return
    }
    try {
      await api('/youtube/channels', { method: 'POST', body: JSON.stringify(newChannel) })
      setStatus('✅ Channel added')
      setShowAdd(false)
      setShowManualAdd(false)
      setNewChannel({ youtube_channel_id: '', name: '', api_key: '', default_duration: 120,
        default_viloud_channel_name: '', default_viloud_id: '', default_cf_input_id: '', enabled: true,
        default_tbtv_channel_id: '' })
      onUpdate()
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
  }

  async function handleSaveEdit() {
    try {
      await api(`/youtube/channels/${editing}`, { method: 'PATCH', body: JSON.stringify(editForm) })
      setStatus('✅ Updated')
      setEditing(null)
      setShowManualEdit(false)
      onUpdate()
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
  }

  async function handleDelete(channelId, channelName) {
    if (!confirm(`Delete "${channelName}" and all its archived videos?`)) return
    try {
      await api(`/youtube/channels/${channelId}`, { method: 'DELETE' })
      setStatus('✅ Deleted')
      onUpdate()
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
  }

  async function handleToggleSync(ch) {
    const newEnabled = !ch.enabled
    try {
      await api(`/youtube/channels/${ch.youtube_channel_id}`, {
        method: 'PATCH',
        body: JSON.stringify({ enabled: newEnabled })
      })
      setStatus(`${ch.name}: sync ${newEnabled ? 'enabled' : 'disabled'}`)
      onUpdate()
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
  }

  function startEdit(ch) {
    setEditing(ch.youtube_channel_id)
    setShowManualEdit(false)
    setEditForm({
      name: ch.name, api_key: ch.api_key || '', default_duration: ch.default_duration || 120,
      default_viloud_channel_name: ch.default_viloud_channel_name || '',
      default_viloud_id: ch.default_viloud_id || '',
      default_cf_input_id: ch.default_cf_input_id || '', enabled: !!ch.enabled,
      default_tbtv_channel_id: ch.default_tbtv_channel_id || '',
      auto_migrate_thumbnails: !!ch.auto_migrate_thumbnails,  // dev24
    })
  }

  async function handleHistorySync() {
    if (!historyTarget) return
    setHistorySyncing(true)
    setStatus(`Syncing ${historyMonths} months of history for ${historyTarget.name}...`)
    try {
      const after = new Date()
      after.setMonth(after.getMonth() - historyMonths)
      const result = await api(`/youtube/sync/${historyTarget.youtube_channel_id}/history`, {
        method: 'POST',
        body: JSON.stringify({ after: after.toISOString() })
      })
      setStatus(`✅ History sync: ${result.added} added, ${result.updated} updated (${result.totalFetched} fetched)`)
      setHistoryTarget(null)
      onUpdate()
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    } finally {
      setHistorySyncing(false)
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="yt-channel-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>YouTube Source Channels</h2>
          <button className="btn-ghost" onClick={onClose}><X size={16} /></button>
        </div>

        {status && <div className={`yt-detail-status ${status.startsWith('Error') ? 'error' : ''}`}>{status}</div>}

        <div className="yt-channel-list">
          {channels.map(ch => (
            <div key={ch.youtube_channel_id} className={`yt-channel-item ${!ch.enabled ? 'disabled' : ''}`}>
              {editing === ch.youtube_channel_id ? (
                <div className="yt-channel-edit-form">
                  <div className="form-row"><label>Name</label>
                    <input value={editForm.name} onChange={e => setEditForm({...editForm, name: e.target.value})} /></div>
                  <div className="form-row"><label>Default Duration (min)</label>
                    <input type="number" value={editForm.default_duration} onChange={e => setEditForm({...editForm, default_duration: e.target.value})} /></div>

                  {/* TBTV Channel Picker — Primary */}
                  <div className="form-row">
                    <label>TBTV Channel</label>
                    <select
                      value={editForm.default_tbtv_channel_id || ''}
                      onChange={e => handleTbtvChannelSelect(e.target.value, setEditForm, editForm)}
                      className="yt-tbtv-picker"
                    >
                      <option value="">— Select TBTV Channel —</option>
                      {tbtvChannels.map(tc => (
                        <option key={tc.viloud_id} value={tc.tbtv_channel_id}>
                          {tc.name} [{tc.channel_type || tc.mode || 'linear'}] ({tc.tbtv_channel_id})
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* CF Live Input Picker */}
                  <div className="form-row">
                    <label>CF Live Input</label>
                    <select
                      value={editForm.default_cf_input_id || ''}
                      onChange={e => setEditForm({...editForm, default_cf_input_id: e.target.value})}
                      className="yt-tbtv-picker"
                    >
                      <option value="">— Select CF Live Input —</option>
                      {cfLiveInputs.map(li => (
                        <option key={li.uid} value={li.uid}>
                          {li.meta?.name || 'Unnamed'} ({li.uid.substring(0, 8)}…)
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Manual override toggle */}
                  <div className="yt-manual-toggle">
                    <button className="btn-ghost btn-xs" onClick={() => setShowManualEdit(!showManualEdit)}>
                      {showManualEdit ? '▾ Hide manual override' : '▸ Manual override'}
                    </button>
                  </div>

                  {showManualEdit && (
                    <>
                      <div className="form-row"><label>Viloud Channel Name</label>
                        <input value={editForm.default_viloud_channel_name} onChange={e => setEditForm({...editForm, default_viloud_channel_name: e.target.value})} /></div>
                      <div className="form-row"><label>Viloud Channel ID</label>
                        <input value={editForm.default_viloud_id} onChange={e => setEditForm({...editForm, default_viloud_id: e.target.value})} /></div>
                      <div className="form-row"><label>CF Live Input ID</label>
                        <input value={editForm.default_cf_input_id} onChange={e => setEditForm({...editForm, default_cf_input_id: e.target.value})} /></div>
                    </>
                  )}

                  <div className="form-row checkbox-row"><label>
                    <input type="checkbox" checked={editForm.enabled} onChange={e => setEditForm({...editForm, enabled: e.target.checked})} /> Enabled
                  </label></div>
                  {/* dev24: opt-in per-source thumbnail migration to CF Images */}
                  <div className="form-row checkbox-row"><label title="On each sync, upload the YouTube thumbnail to Cloudflare Images (16:9). Off by default. Roku-spec-compliant thumbnails will land in the catalog without a manual migration step.">
                    <input type="checkbox" checked={editForm.auto_migrate_thumbnails || false}
                      onChange={e => setEditForm({...editForm, auto_migrate_thumbnails: e.target.checked})} /> Auto-migrate thumbnails to CF Images (16:9)
                  </label></div>
                  <div className="yt-channel-edit-actions">
                    <button className="btn-primary" onClick={handleSaveEdit}><Save size={12} /> Save</button>
                    <button className="btn-ghost" onClick={() => setEditing(null)}>Cancel</button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="yt-channel-info">
                    <div className="yt-channel-name">{ch.name}</div>
                    <div className="yt-channel-id">{ch.youtube_channel_id}</div>
                    <div className="yt-channel-meta">
                      {ch.default_tbtv_channel_id ? (
                        <span title={`TBTV: ${ch.default_tbtv_channel_id}`}>📺 {getTbtvChannelDisplay(ch.default_tbtv_channel_id) || ch.default_viloud_channel_name}</span>
                      ) : ch.default_viloud_channel_name ? (
                        <span>📺 {ch.default_viloud_channel_name}</span>
                      ) : null}
                      <span>⏱ {ch.default_duration}min</span>
                      {ch.default_cf_input_id && <span title={ch.default_cf_input_id}>📡 {getCfInputDisplay(ch.default_cf_input_id)}</span>}
                      {ch.last_synced_at && <span>🔄 {new Date(ch.last_synced_at).toLocaleDateString()}</span>}
                    </div>
                  </div>
                  <div className="yt-channel-sync-toggle" title={ch.enabled ? 'Sync enabled — click to disable' : 'Sync disabled — click to enable'}>
                    <label className="sync-switch">
                      <input
                        type="checkbox"
                        checked={!!ch.enabled}
                        onChange={() => handleToggleSync(ch)}
                      />
                      <span className="sync-slider" />
                    </label>
                    <span className={`sync-label ${ch.enabled ? 'on' : 'off'}`}>
                      {ch.enabled ? 'Sync on' : 'Sync off'}
                    </span>
                  </div>
                  <div className="yt-channel-actions">
                    <button className="btn-ghost btn-sm" onClick={() => onSync(ch.youtube_channel_id)} disabled={syncing} title="Sync upcoming/live">
                      <RefreshCw size={14} className={syncing ? 'spinning' : ''} />
                    </button>
                    <button className="btn-ghost btn-sm" onClick={() => setHistoryTarget(ch)} disabled={historySyncing} title="Sync history">
                      <History size={14} />
                    </button>
                    <button className="btn-ghost btn-sm" onClick={() => startEdit(ch)} title="Edit">
                      <Edit2 size={14} />
                    </button>
                    <button className="btn-ghost btn-sm" onClick={() => handleDelete(ch.youtube_channel_id, ch.name)} title="Delete">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>

        {/* Add Channel Form */}
        {showAdd ? (
          <div className="yt-add-form">
            <h3>Add YouTube Channel</h3>
            <div className="form-row"><label>YouTube Channel ID *</label>
              <input value={newChannel.youtube_channel_id} onChange={e => setNewChannel({...newChannel, youtube_channel_id: e.target.value})} placeholder="UCxxxxxxxx" /></div>
            <div className="form-row"><label>Display Name *</label>
              <input value={newChannel.name} onChange={e => setNewChannel({...newChannel, name: e.target.value})} placeholder="Channel name" /></div>
            <div className="form-row"><label>Default Duration (min)</label>
              <input type="number" value={newChannel.default_duration} onChange={e => setNewChannel({...newChannel, default_duration: e.target.value})} /></div>

            {/* TBTV Channel Picker — Primary */}
            <div className="form-row">
              <label>TBTV Channel</label>
              <select
                value={newChannel.default_tbtv_channel_id || ''}
                onChange={e => handleTbtvChannelSelect(e.target.value, setNewChannel, newChannel)}
                className="yt-tbtv-picker"
              >
                <option value="">— Select TBTV Channel —</option>
                {tbtvChannels.map(tc => (
                  <option key={tc.viloud_id} value={tc.tbtv_channel_id}>
                    {tc.name} [{tc.channel_type || tc.mode || 'linear'}] ({tc.tbtv_channel_id})
                  </option>
                ))}
              </select>
            </div>

            {/* CF Live Input Picker */}
            <div className="form-row">
              <label>CF Live Input</label>
              <select
                value={newChannel.default_cf_input_id || ''}
                onChange={e => setNewChannel({...newChannel, default_cf_input_id: e.target.value})}
                className="yt-tbtv-picker"
              >
                <option value="">— Select CF Live Input —</option>
                {cfLiveInputs.map(li => (
                  <option key={li.uid} value={li.uid}>
                    {li.meta?.name || 'Unnamed'} ({li.uid.substring(0, 8)}…)
                  </option>
                ))}
              </select>
            </div>

            {/* Manual override toggle */}
            <div className="yt-manual-toggle">
              <button className="btn-ghost btn-xs" onClick={() => setShowManualAdd(!showManualAdd)}>
                {showManualAdd ? '▾ Hide manual override' : '▸ Manual override'}
              </button>
            </div>

            {showManualAdd && (
              <>
                <div className="form-row"><label>Viloud Channel Name</label>
                  <input value={newChannel.default_viloud_channel_name} onChange={e => setNewChannel({...newChannel, default_viloud_channel_name: e.target.value})} /></div>
                <div className="form-row"><label>Viloud Channel ID</label>
                  <input value={newChannel.default_viloud_id} onChange={e => setNewChannel({...newChannel, default_viloud_id: e.target.value})} /></div>
                <div className="form-row"><label>CF Live Input ID</label>
                  <input value={newChannel.default_cf_input_id} onChange={e => setNewChannel({...newChannel, default_cf_input_id: e.target.value})} /></div>
              </>
            )}

            {/* dev24: opt-in per-source thumbnail migration to CF Images */}
            <div className="form-row checkbox-row"><label title="On each sync, upload the YouTube thumbnail to Cloudflare Images (16:9). Off by default. Roku-spec-compliant thumbnails will land in the catalog without a manual migration step.">
              <input type="checkbox" checked={newChannel.auto_migrate_thumbnails}
                onChange={e => setNewChannel({...newChannel, auto_migrate_thumbnails: e.target.checked})} /> Auto-migrate thumbnails to CF Images (16:9)
            </label></div>

            <div className="yt-add-actions">
              <button className="btn-primary" onClick={handleAdd}><Plus size={14} /> Add Channel</button>
              <button className="btn-ghost" onClick={() => { setShowAdd(false); setShowManualAdd(false) }}>Cancel</button>
            </div>
          </div>
        ) : (
          <button className="btn-ghost yt-add-btn" onClick={() => setShowAdd(true)}>
            <Plus size={14} /> Add Channel
          </button>
        )}

        {/* History Sync Dialog */}
        {historyTarget && (
          <div className="yt-history-dialog">
            <h3><History size={14} /> Sync History: {historyTarget.name}</h3>
            <p className="muted">Pull all videos (including completed streams) from this channel's YouTube history.</p>
            <div className="form-row">
              <label>How far back?</label>
              <select value={historyMonths} onChange={e => setHistoryMonths(parseInt(e.target.value))}>
                <option value={1}>1 month</option>
                <option value={3}>3 months</option>
                <option value={6}>6 months</option>
                <option value={12}>12 months</option>
                <option value={24}>24 months</option>
              </select>
            </div>
            <div className="yt-add-actions">
              <button className="btn-primary" onClick={handleHistorySync} disabled={historySyncing}>
                {historySyncing ? <><RefreshCw size={12} className="spinning" /> Syncing...</> : <><History size={14} /> Sync History</>}
              </button>
              <button className="btn-ghost" onClick={() => setHistoryTarget(null)} disabled={historySyncing}>Cancel</button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
